import { gql } from "@apollo/client";
import { useRouter } from "next/router";
import { HTMLProps } from "react";
import { match, P } from "ts-pattern";

import { ModalComponent } from "modules/modal/modal.component";
import { useFunnelContext } from "shared/Funnel/FunnelContextProvider";
import { useSwapActiveOrder } from "shared/SwapActiveOrder/useSwapActiveOrder";
import { useTrackOrder } from "shared/analytics/useTrackOrder";
import { useTrackProduct } from "shared/analytics/useTrackProduct";
import { useTrackSwap } from "shared/analytics/useTrackSwap";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { Button } from "ui/Button";
import { getCookie, setToken } from "utils/cookies.utils";
import { useModalState } from "utils/useModalState";
import { usePriceAdvantage } from "utils/usePriceAdvantage";
import { useI18n } from "utils/with-i18n.utils";

import { ProductReminderModal } from "../ProductSpecifications/shared/ProductReminderModal";

import {
  useCreateNewOrderMutation,
  useProductCreateSwapMutation,
} from "./index.generated";

gql`
  mutation createNewOrder($orderAttributes: DraftOrderDto!) {
    result: nest_createDraftOrder(orderAttributes: $orderAttributes) {
      success
      orderId
      session {
        type
        token
      }
    }
  }

  mutation productCreateSwap($nextSubSku: String!, $orderId: Int!) {
    result: nest_orderSwap_createSwap(sku: $nextSubSku, orderId: $orderId) {
      orderId
    }
  }
`;

enum Errors {
  MultipleOrdersNotAllowed = "MULTIPLE_ORDERS_NOT_ALLOWED",
  OperationInProgress = "OPERATION_IN_PROGRESS",
}

type ProductOrderButtonProps = {
  className?: HTMLProps<HTMLDivElement>["className"];
};

export const ProductOrderButton = ({ className }: ProductOrderButtonProps) => {
  const i18n = useI18n();
  const { push } = useRouter();
  const { trackProductAdded } = useTrackProduct();
  const { trackCheckoutStarted } = useTrackOrder();
  const { trackSwapFunnelStarted } = useTrackSwap();

  const { isOpen, closeModal, openModal } = useModalState();
  const {
    isOpen: isErrorModalOpen,
    closeModal: closeErrorModal,
    openModal: openErrorModal,
  } = useModalState();
  const { currentVariant, isPreOrder } = useProductPageContext();
  const { isSwap } = useFunnelContext();
  const { orderId } = useSwapActiveOrder({});
  const { discountInfo } = usePriceAdvantage({
    originalUpfrontPrice: currentVariant?.originalUpfrontPrice,
    upfrontPrice: currentVariant?.upfrontPrice,
  });

  const subscriptionSku = currentVariant?.sku;

  const [createNewOrder, { loading: isCreateNewOrderLoading }] =
    useCreateNewOrderMutation();
  const [createSwap, { loading: isCreateSwapLoading }] =
    useProductCreateSwapMutation();

  const isCreatingOrder = isCreateNewOrderLoading || isCreateSwapLoading;

  const handleCreateNewOrder = () => {
    match({ subscriptionSku, isSwap, orderId })
      .with(
        { isSwap: false, subscriptionSku: P.select(P.not(P.nullish)) },
        async (sku) => {
          try {
            const { data } = await createNewOrder({
              variables: {
                orderAttributes: {
                  sku,
                  attribution: getCookie("_te"),
                },
              },
            });

            const token = data?.result?.session?.token;

            if (token) {
              setToken(data?.result.session.token);
            }

            if (!isSwap && data?.result.orderId) {
              trackProductAdded(sku);
              trackCheckoutStarted(data.result.orderId);
            }

            return push("/commander/recapitulatif");
          } catch (error) {
            return match(error)
              .with({ message: Errors.MultipleOrdersNotAllowed }, () =>
                push("/espace-membre"),
              )
              .otherwise(() => {
                throw error;
              });
          }
        },
      )
      .with(
        {
          isSwap: true,
          subscriptionSku: P.not(P.nullish),
          orderId: P.not(P.nullish),
        },
        async ({ subscriptionSku: sku, orderId: activeOrderId }) => {
          try {
            const { data } = await createSwap({
              variables: {
                orderId: activeOrderId,
                nextSubSku: sku,
              },
            });

            if (data?.result.orderId) {
              trackSwapFunnelStarted(data.result.orderId);
            }

            return push("/swap/recapitulatif");
          } catch (error: any) {
            const errorMessage = error?.message;

            if (errorMessage?.includes(Errors.OperationInProgress)) {
              openErrorModal();

              return;
            }

            // Re-throw other errors
            throw error;
          }
        },
      )
      .run();
  };

  return (
    <>
      {!!currentVariant?.id && (
        <ProductReminderModal
          isOpen={isOpen}
          closeModal={closeModal}
          subscriptionId={currentVariant.id}
        />
      )}

      <ModalComponent isOpen={isErrorModalOpen} onClose={closeErrorModal}>
        <div className="p-6 text-center">
          <h2 className="mb-4 text-2xl font-bold">
            {i18n.t("product.inProgressOperation.title")}
          </h2>

          <p className="mb-4">
            {i18n.t(
              "product.inProgressOperation.description",
              {},
              { html: true },
            )}
          </p>

          <Button onClick={closeErrorModal}>
            {i18n.t("product.inProgressOperation.close")}
          </Button>
        </div>
      </ModalComponent>

      {isPreOrder ? (
        <Button
          className={className}
          onClick={handleCreateNewOrder}
          isLoading={isCreatingOrder}
          disabled={!subscriptionSku || (isSwap && !discountInfo)}
        >
          {i18n.t("product.preOrder.button")}
        </Button>
      ) : currentVariant?.isInStock ? (
        <Button
          className={className}
          onClick={handleCreateNewOrder}
          isLoading={isCreatingOrder}
          disabled={!subscriptionSku || (isSwap && !discountInfo)}
        >
          {i18n.t("product.available.iOrder")}
        </Button>
      ) : (
        <Button className={className} onClick={openModal}>
          {i18n.t("product.outOtStock.warnMe")}
        </Button>
      )}
    </>
  );
};
